// Utility functions for time formatting and calculations

/**
 * Returns a relative time string (e.g., "5 minutes ago", "2 hours ago")
 * @param {string|Date|Object} timestamp - The timestamp to format (string, Date, or Firestore Timestamp)
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp) => {
  if (!timestamp) return 'unknown';

  let time;
  // Handle Firestore Timestamp object
  if (typeof timestamp.toDate === 'function') {
    time = timestamp.toDate();
  } else if (timestamp._seconds !== undefined) {
    // Handle serialized Firestore Timestamp ({ _seconds, _nanoseconds }) from callable functions
    time = new Date(timestamp._seconds * 1000);
  } else if (timestamp.seconds !== undefined) {
    // Handle protobuf-style Timestamp ({ seconds, nanoseconds })
    time = new Date(timestamp.seconds * 1000);
  } else {
    time = new Date(timestamp);
  }

  // Check if date is valid
  if (isNaN(time.getTime())) return 'unknown';

  const now = new Date();
  const diffMs = now - time;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return time.toLocaleDateString();
};

/**
 * Checks if a like count hits a milestone (5, 10, 25, 50, or factors of 100)
 * @param {number} likeCount - The number of likes
 * @returns {number|null} The milestone number or null if no milestone
 */
export const getLikeMilestone = (likeCount) => {
  const milestones = [5, 10, 25, 50];
  for (let i = milestones.length - 1; i >= 0; i--) {
    if (likeCount >= milestones[i]) {
      // Check if it's exactly a milestone or a factor of 100
      if (likeCount === milestones[i] || (likeCount >= 100 && likeCount % 100 === 0)) {
        return milestones[i];
      }
    }
  }
  return null;
};

/**
 * Gets location display for a restroom, handling various address formats
 * @param {Object} restroom - The restroom object
 * @returns {string} Formatted location string
 */
export const getRestroomLocation = (restroom) => {
  // Try address first
  if (restroom.address) {
    const location = getLocationFromAddress(restroom.address);
    if (location !== 'Location unknown') {
      return location;
    }
  }
  
  // If we have coordinates but no proper address, show generic location
  if (restroom.latitude && restroom.longitude) {
    return 'Location';
  }
  
  // Fallback
  return 'Location unknown';
};

/**
 * Extracts city and state from an address string
 * @param {string} address - The full address
 * @returns {string} Formatted "City, State" string
 */
export const getLocationFromAddress = (address) => {
  if (!address) return 'Location unknown';
  
  // Clean up the address first
  const cleanAddress = address.trim();
  
  // If it looks like coordinates, return a generic location
  if (cleanAddress.match(/^-?\d+\.\d+,\s*-?\d+\.\d+$/)) {
    return 'Location';
  }
  
  // Try different parsing strategies
  const parts = cleanAddress.split(',');
  
  if (parts.length >= 2) {
    const city = parts[0].trim();
    const statePart = parts[1].trim();
    
    // Extract state abbreviation (2 letters) or full state name
    const stateMatch = statePart.match(/\b([A-Z]{2}|[A-Za-z\s]+)\b/);
    const state = stateMatch ? stateMatch[1] : '';
    
    // If we have both city and state, return them
    if (city && state) {
      return `${city}, ${state}`;
    }
    
    // If we only have city, return it
    if (city) {
      return city;
    }
  }
  
  // If splitting by comma didn't work, try other strategies
  // Look for common patterns like "City ST" or "City, ST"
  const cityStateMatch = cleanAddress.match(/^([^,]+),?\s*([A-Z]{2}|[A-Za-z\s]+)$/);
  if (cityStateMatch) {
    const [, city, state] = cityStateMatch;
    return `${city}, ${state}`;
  }
  
  // If all else fails, return a shortened version of the address
  if (cleanAddress.length > 30) {
    return cleanAddress.substring(0, 30) + '...';
  }
  
  return cleanAddress;
};

/**
 * Formats a rating to poop emojis with rating text
 * @param {number} rating - The rating to format
 * @returns {string} Poop emoji rating string with rating text
 */
export const formatRating = (rating) => {
  if (typeof rating !== 'number' || isNaN(rating)) return '💩 (5.0/5.0)';
  
  const roundedRating = Math.round(rating);
  const poopEmojis = '💩'.repeat(Math.max(0, Math.min(5, roundedRating)));
  
  return `${poopEmojis} (${rating.toFixed(1)}/5.0)`;
};
